import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { create } from 'zustand';
import { Icon, type IconName } from './icons';
import { useApp, useToasts } from '../data/store';
import { ColourButton } from './pickers';

/* ---------- Segmented control with a sliding thumb ---------- */

export function Segmented<T extends string>({ value, options, onChange, size, fixed, label, className }: {
  value: T; options: Array<{ value: T; label: ReactNode; disabled?: boolean }>; onChange: (v: T) => void; size?: 'sm'; fixed?: boolean; label: string; className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState<{ x: number; w: number } | null>(null);
  const measure = useCallback(() => {
    const root = ref.current;
    const btn = root?.querySelector<HTMLButtonElement>('button[aria-pressed="true"]');
    if (!root || !btn) { setThumb(null); return; }
    setThumb({ x: btn.offsetLeft, w: btn.offsetWidth });
  }, []);
  useLayoutEffect(measure, [measure, value, options.length]);
  useEffect(() => {
    const ro = new ResizeObserver(measure);
    if (ref.current) ro.observe(ref.current);
    return () => ro.disconnect();
  }, [measure]);
  return (
    <div ref={ref} className={`seg${size ? ' ' + size : ''}${fixed ? ' fixed' : ''}${className ? ' ' + className : ''}`} role="group" aria-label={label}>
      {thumb && <span className="thumb" style={{ width: thumb.w, transform: `translateX(${thumb.x - 3}px)`, left: 3 }} aria-hidden />}
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={o.value === value} disabled={o.disabled} onClick={() => o.value !== value && onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

/* ---------- Presence: stay on screen while animating out ---------- */

/** How long sheets, pop-ups and dialogs take to animate away. Matches --dur-out in app.css. */
export const EXIT_MS = 200;
/** Keeps something rendered for a moment after it's closed, so it can animate out instead of vanishing. */
export function usePresence(open: boolean, ms = EXIT_MS) {
  const [shown, setShown] = useState(open);
  useEffect(() => {
    if (open) { setShown(true); return; }
    const t = setTimeout(() => setShown(false), ms);
    return () => clearTimeout(t);
  }, [open, ms]);
  return { shown: open || shown, leaving: !open && shown };
}

/** Set by the sheet host while the current sheet animates away (sheets there are unmounted when closed). */
export const SheetClosing = createContext(false);

/* ---------- Stepper ---------- */

export function Stepper({ value, min, max, onChange, label }: { value: number; min: number; max: number; onChange: (v: number) => void; label: string }) {
  return (
    <div className="stepper" role="group" aria-label={label}>
      <button type="button" aria-label={`Fewer ${label.toLowerCase()}`} disabled={value <= min} onClick={() => onChange(Math.max(min, value - 1))}><Icon name="minus" size={14} stroke={2.4} /></button>
      <output aria-live="polite">{value}</output>
      <button type="button" aria-label={`More ${label.toLowerCase()}`} disabled={value >= max} onClick={() => onChange(Math.min(max, value + 1))}><Icon name="plus" size={14} stroke={2.4} /></button>
    </div>
  );
}

/* ---------- Switch ---------- */

export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return <button type="button" role="switch" className="switch" aria-checked={checked} aria-label={label} disabled={disabled} onClick={() => onChange(!checked)} />;
}

/* ---------- Buttons ---------- */

export function IconButton({ icon, label, onClick, size, className, disabled, badge, iconSize, ...rest }: {
  icon: IconName; label: string; onClick?: () => void; size?: 'sm' | 'lg'; className?: string; disabled?: boolean; badge?: number; iconSize?: number;
  ref?: React.Ref<HTMLButtonElement>;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'>) {
  return (
    <button type="button" className={`icon-btn${size ? ' ' + size : ''}${className ? ' ' + className : ''}`} aria-label={label} title={label} onClick={onClick} disabled={disabled} {...rest}>
      <Icon name={icon} size={iconSize ?? (size === 'sm' ? 18 : 20)} />
      {badge ? <span className="badge" aria-label={`${badge} unread`}>{badge > 99 ? '99+' : badge}</span> : null}
    </button>
  );
}

/* ---------- Sheet (modal dialog) ---------- */

export function Sheet({ open, onClose, title, subtitle, children, footer, size, labelledBy, closeLabel = 'Close', dock }: {
  open: boolean; onClose: () => void; title: ReactNode; subtitle?: ReactNode; children?: ReactNode; footer?: ReactNode; size?: 'wide' | 'xl'; labelledBy?: string; closeLabel?: string;
  /**
   * Docked to the left edge with nothing over the rest of the page, so the screen stays in view while you work
   * (live appeals, urgent messages, announcements). The page behind can still be used.
   */
  dock?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const hostClosing = useContext(SheetClosing);
  const visible = open && !hostClosing;
  const { shown, leaving } = usePresence(visible);
  const titleId = labelledBy ?? `sheet-${String(title).replace(/\W+/g, '-').toLowerCase()}`;
  useEffect(() => {
    if (!visible) return;
    const prev = document.activeElement as HTMLElement | null;
    const node = ref.current;
    const first = node?.querySelector<HTMLElement>('[autofocus],input,select,textarea,button:not([data-close])');
    (first ?? node)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
      if (e.key === 'Tab' && node && !dock) {
        const f = [...node.querySelectorAll<HTMLElement>('button,input,select,textarea,a[href],[tabindex]:not([tabindex="-1"])')].filter((x) => !x.hasAttribute('disabled'));
        if (!f.length) return;
        const a = f[0]!, z = f[f.length - 1]!;
        if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus(); }
        else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('keydown', onKey, true); prev?.focus?.(); };
  }, [visible, onClose, dock]);
  if (!shown) return null;
  if (dock) return createPortal(
    <div className={`dock-wrap${leaving ? ' out' : ''}`}>
      <div ref={ref} className="sheet dock" role="dialog" aria-modal="false" aria-labelledby={titleId} tabIndex={-1}>
        <div className="sheet-head">
          <div style={{ flex: 1, minWidth: 0 }}><h2 id={titleId}>{title}</h2>{subtitle && <p>{subtitle}</p>}</div>
          <IconButton icon="x" label={closeLabel} size="sm" className="plain-close" onClick={onClose} data-close="" />
        </div>
        {children}
        {footer && <div className="sheet-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
  return createPortal(
    <div className={`scrim${leaving ? ' out' : ''}`} onMouseDown={(e) => { if (!leaving && e.target === e.currentTarget) onClose(); }}>
      <div ref={ref} className={`sheet${size ? ' ' + size : ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <div className="sheet-head">
          <div style={{ flex: 1, minWidth: 0 }}><h2 id={titleId}>{title}</h2>{subtitle && <p>{subtitle}</p>}</div>
          <IconButton icon="x" label={closeLabel} size="sm" className="plain-close" onClick={onClose} data-close="" />
        </div>
        {children}
        {footer && <div className="sheet-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/* ---------- Popover anchored to an element ---------- */

export function Popover({ anchor, open, onClose, children, width = 380, align = 'end', label }: {
  anchor: HTMLElement | null; open: boolean; onClose: () => void; children: ReactNode; width?: number; align?: 'start' | 'end'; label: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const { shown, leaving } = usePresence(open, 150);
  const [pos, setPos] = useState<{ left: number; top: number; maxH: number } | null>(null);
  // Two passes: render off-screen to measure, then place below the anchor if it fits, else above, else wherever it fits best.
  useLayoutEffect(() => {
    if (!open || !anchor) { if (!shown) setPos(null); return; }
    const place = () => {
      const r = anchor.getBoundingClientRect();
      const h = ref.current?.offsetHeight ?? 400;
      const vh = window.innerHeight;
      const left = Math.max(12, Math.min(window.innerWidth - width - 12, align === 'end' ? r.right - width : r.left));
      let top: number;
      if (r.bottom + 10 + h <= vh - 12) top = r.bottom + 10;
      else if (r.top - 10 - h >= 12) top = r.top - 10 - h;
      else top = Math.max(12, vh - 12 - h);
      setPos({ left, top, maxH: vh - top - 12 });
    };
    place();
    const id = requestAnimationFrame(place);
    window.addEventListener('resize', place);
    return () => { cancelAnimationFrame(id); window.removeEventListener('resize', place); };
  }, [open, anchor, width, align, shown]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { const t = e.target as Element; if (ref.current && !ref.current.contains(t) && !anchor?.contains(t) && !t.closest?.('[data-popover-child]')) onClose(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); anchor?.focus(); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey, true); };
  }, [open, onClose, anchor]);
  if (!shown) return null;
  return createPortal(
    <div ref={ref} className={`popover${leaving ? ' out' : ''}`} role="dialog" aria-label={label} style={pos ? { left: pos.left, top: pos.top, width, maxHeight: pos.maxH, overflowX: 'hidden', overflowY: 'auto' } : { left: -9999, top: 0, width, visibility: 'hidden' }}>{children}</div>,
    document.body,
  );
}

/* ---------- Confirm dialog (promise based) ---------- */

interface Choice { value: string; label: string; kind?: 'primary' | 'plain' | 'danger' }
interface ConfirmReq { title: string; body?: ReactNode; choices: Choice[]; resolve: (v: string | null) => void }
const useConfirm = create<{ req: ConfirmReq | null }>(() => ({ req: null }));
/** Asks a question with your own buttons. Resolves to the chosen value, or null if closed. */
export function choose(title: string, opts: { body?: ReactNode; choices: Choice[] }): Promise<string | null> {
  return new Promise((resolve) => useConfirm.setState({ req: { title, body: opts.body, choices: opts.choices, resolve } }));
}
interface AskReq { title: string; label: string; value: string; ok: string; resolve: (v: string | null) => void }
const useAsk = create<{ req: AskReq | null }>(() => ({ req: null }));
/** Asks for a short piece of text (e.g. a name). Resolves to the text, or null if cancelled. */
export function askText(title: string, opts: { label: string; value?: string; ok?: string }): Promise<string | null> {
  return new Promise((resolve) => useAsk.setState({ req: { title, label: opts.label, value: opts.value ?? '', ok: opts.ok ?? 'Save', resolve } }));
}
export function AskHost() {
  const live = useAsk((s) => s.req);
  const last = useRef(live);
  if (live) last.current = live;
  const req = live ?? last.current;
  const [v, setV] = useState('');
  useEffect(() => { if (live) setV(live.value ?? ''); }, [live]);
  const close = (x: string | null) => { if (!live) return; live.resolve(x); useAsk.setState({ req: null }); };
  return (
    <Sheet open={!!live} onClose={() => close(null)} title={req?.title ?? ''}
      footer={<><button type="button" className="btn plain" onClick={() => close(null)}>Cancel</button>
        <button type="button" className="btn primary" disabled={!v.trim()} onClick={() => close(v.trim())}>{req?.ok}</button></>}>
      <form onSubmit={(e) => { e.preventDefault(); if (v.trim()) close(v.trim()); }}>
        <label className="field"><span>{req?.label}</span><input className="input" autoFocus maxLength={40} value={v} onChange={(e) => setV(e.target.value)} /></label>
      </form>
    </Sheet>
  );
}

export async function confirm(title: string, opts: { body?: string; ok?: string; danger?: boolean } = {}): Promise<boolean> {
  return (await choose(title, { body: opts.body, choices: [{ value: 'no', label: 'Cancel', kind: 'plain' }, { value: 'ok', label: opts.ok ?? 'OK', kind: opts.danger ? 'danger' : 'primary' }] })) === 'ok';
}
export function ConfirmHost() {
  const live = useConfirm((s) => s.req);
  // Keep showing the last question while the dialog animates away.
  const last = useRef(live);
  if (live) last.current = live;
  const req = live ?? last.current;
  const close = (v: string | null) => { if (!live) return; live.resolve(v); useConfirm.setState({ req: null }); };
  return (
    <Sheet open={!!live} onClose={() => close(null)} title={req?.title ?? ''} subtitle={req?.body}
      footer={<>{req?.choices.map((c, i) => (
        <button key={c.value} type="button" className={`btn ${c.kind ?? 'plain'}`} onClick={() => close(c.value)} autoFocus={i === req.choices.length - 1}>{c.label}</button>
      ))}</>} />
  );
}

/* ---------- Toasts ---------- */

export function Toasts() {
  const list = useToasts((s) => s.list);
  return (
    <div className="toasts" role="status" aria-live="polite">
      {list.map((t) => (
        <div key={t.id} className={`toast${t.bad ? ' bad' : ''}${t.leaving ? ' out' : ''}`}>
          <Icon name={t.bad ? 'alert' : 'check'} size={18} />
          <span>{t.text}</span>
          {t.action && <button type="button" className="btn sm plain" onClick={t.action.run}>{t.action.label}</button>}
        </div>
      ))}
    </div>
  );
}

/* ---------- Grouped list row (Apple-style settings rows) ---------- */

export function Row({ icon, label, children, onClick }: { icon: IconName; label: string; children?: ReactNode; onClick?: () => void }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag className="gi" type={onClick ? 'button' : undefined} onClick={onClick}>
      <span className="ic"><Icon name={icon} size={20} /></span>
      <span className="k">{label}</span>
      <span className="v">{children}{onClick && <Icon name="chevron" size={16} />}</span>
    </Tag>
  );
}

/* ---------- Slider ---------- */

export function Slider({ value, min, max, step = 1, onChange, label, format, ends }: {
  value: number; min: number; max: number; step?: number; onChange: (v: number) => void; label: string; format?: (v: number) => string; ends?: [string, string];
}) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="slider">
      <input type="range" min={min} max={max} step={step} value={value} aria-label={label} aria-valuetext={format?.(value)}
        style={{ ['--p' as string]: `${pct}%` }} onChange={(e) => onChange(Number(e.target.value))} />
      <div className="slider-meta">{ends ? <span>{ends[0]}</span> : <span />}<output>{format ? format(value) : value}</output>{ends ? <span>{ends[1]}</span> : <span />}</div>
    </div>
  );
}

/* ---------- Colour choice: an "automatic" option, swatches, and a hex field with a picker ---------- */

export function ColourChoice({ value, onChange, swatches, autoLabel, label, keyPrefix }: {
  value: string | null | undefined; onChange: (v: string | null, key?: string) => void; swatches: readonly string[]; autoLabel?: string; label: string; keyPrefix?: string;
}) {
  const cur = value?.toUpperCase() ?? null;
  const [text, setText] = useState<string | null>(null);
  // The masjid's own colours (Settings › Masjid) come first, offered but never chosen for the user.
  const brand = useApp((x) => x.ws?.masjid.colors) ?? [];
  return (
    <div className="colour-choice">
      {brand.length > 0 && (
        <div className="brand-row">
          <small className="brand-label">Masjid colours</small>
          <div className="swatches" role="radiogroup" aria-label={`${label}: masjid colours`}>
            {brand.map((c) => (
              <button key={c} type="button" role="radio" aria-checked={cur === c} aria-label={`Masjid colour ${c}`} title={`Masjid colour ${c}`} className={`sw${cur === c ? ' on' : ''}`} style={{ background: c }} onClick={() => onChange(c)} />
            ))}
          </div>
        </div>
      )}
      <div className="swatches" role="radiogroup" aria-label={label}>
        {autoLabel && <button type="button" role="radio" aria-checked={!cur} aria-label={autoLabel} title={autoLabel} className={`sw auto${!cur ? ' on' : ''}`} onClick={() => onChange(null)} />}
        {swatches.map((c) => (
          <button key={c} type="button" role="radio" aria-checked={cur === c} aria-label={c} title={c} className={`sw${cur === c ? ' on' : ''}`} style={{ background: c }} onClick={() => onChange(c)} />
        ))}
      </div>
      <label className="hex">
        <span className="h">#</span>
        <input className="bare mono" aria-label={`${label}, hex code`} maxLength={6} placeholder={autoLabel ? 'Automatic' : ''} value={text ?? (cur ? cur.slice(1) : '')}
          onChange={(e) => { const t = e.target.value.replace(/[^0-9a-f]/gi, '').slice(0, 6); setText(t); if (t.length === 6) onChange(`#${t.toUpperCase()}`, `${keyPrefix ?? label}:hex`); }}
          onBlur={() => setText(null)} />
        <ColourButton label={`Pick ${label.toLowerCase()}`} value={cur ?? '#0A84FF'} onChange={(hex) => onChange(hex, `${keyPrefix ?? label}:pick`)} />
      </label>
    </div>
  );
}

/* ---------- Number with preset choices (text size) ---------- */

export function PresetNumber({ value, min, max, presets, onChange, label, unit }: {
  value: number; min: number; max: number; presets: readonly number[]; onChange: (v: number, key?: string) => void; label: string; unit?: string;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const clampV = (n: number) => Math.max(min, Math.min(max, Math.round(n)));
  const [pos, setPos] = useState<{ left: number; top: number; maxH: number } | null>(null);
  useLayoutEffect(() => {
    if (!open || !root.current) { setPos(null); return; }
    const r = root.current.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - 12, above = r.top - 12;
    const maxH = Math.min(260, Math.max(below, above) - 6);
    setPos({ left: r.left + r.width / 2 - 48, top: below >= 180 || below >= above ? r.bottom + 6 : r.top - 6 - maxH, maxH });
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { const t = e.target as Node; if (!root.current?.contains(t) && !list.current?.contains(t)) setOpen(false); };
    // Capture phase, so the surrounding popover doesn't treat a click on the list as "outside".
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [open]);
  useEffect(() => {
    if (!pos || !list.current) return;
    const sel = list.current.querySelector<HTMLElement>('[aria-selected="true"]') ?? [...list.current.children].find((c) => Number(c.textContent) >= value) as HTMLElement | undefined;
    if (sel) list.current.scrollTop = sel.offsetTop - list.current.clientHeight / 2 + sel.offsetHeight / 2;
  }, [pos, value]);
  const commit = () => { if (text != null) { const n = parseInt(text, 10); if (Number.isFinite(n)) onChange(clampV(n), 'preset'); } setText(null); };
  return (
    <div ref={root} className="preset-num" role="group" aria-label={label}>
      <button type="button" aria-label={`Smaller ${label.toLowerCase()}`} disabled={value <= min} onClick={() => onChange(clampV(value - 1), 'step')}><Icon name="minus" size={14} stroke={2.4} /></button>
      <input className="bare mono" inputMode="numeric" aria-label={label} aria-expanded={open} aria-haspopup="listbox" value={text ?? String(value)}
        onFocus={(e) => { setOpen(true); e.currentTarget.select(); }} onClick={() => setOpen(true)}
        onChange={(e) => setText(e.target.value.replace(/\D/g, '').slice(0, 3))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); setOpen(false); (e.target as HTMLInputElement).blur(); }
          if (e.key === 'Escape') { e.stopPropagation(); setText(null); setOpen(false); }
          if (e.key === 'ArrowUp') { e.preventDefault(); onChange(clampV(value + 1), 'step'); }
          if (e.key === 'ArrowDown') { e.preventDefault(); onChange(clampV(value - 1), 'step'); }
        }} />
      {unit && <span className="unit">{unit}</span>}
      <button type="button" aria-label={`Bigger ${label.toLowerCase()}`} disabled={value >= max} onClick={() => onChange(clampV(value + 1), 'step')}><Icon name="plus" size={14} stroke={2.4} /></button>
      {open && createPortal(
        <ul ref={list} className="preset-list" role="listbox" aria-label={`${label} sizes`} data-popover-child=""
          style={pos ? { left: pos.left, top: pos.top, maxHeight: pos.maxH } : { visibility: 'hidden' }}>
          {presets.map((p) => (
            <li key={p} role="option" aria-selected={p === value} onMouseDown={(e) => { e.preventDefault(); setText(null); onChange(p, 'preset'); setOpen(false); }}>{p}</li>
          ))}
        </ul>, document.body,
      )}
    </div>
  );
}
